import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  getTodasAsignaciones, getPlanes, getAllPlayersWithGroups, getFamilias, getAllGroups,
  getPlanesDeJugadores, getInscritosPorGrupo, getJugadoresConAdeudo, getOrgDeportes,
  getPromociones, getPromocionesDeJugadores,
} from '../../../../lib/supabase'
import { isVisiblePlayer, isActiveGroupRelation } from '../../../../lib/playerVisibility'
import { useOrganization } from '../../../../contexts/OrganizationContext'

// Toda la carga de la pantalla de Asignaciones en un solo lugar, para que el
// componente se quede orquestando (seleccion, unidad, dialogo) y se pueda leer
// de un tiron.

const VACIO = {
  asignaciones: [],
  planes: [],
  planesDe: {},
  jugadores: [],
  familias: [],
  grupos: [],
  inscritosPorGrupo: {},
  adeudoDe: new Set(),
  orgDeportes: [],
  promociones: [],
  promocionesDe: {},
}

export const useAsignacionesData = (temporadaId) => {
  const { org } = useOrganization()
  const [data, setData] = useState(VACIO)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!temporadaId) {
      setData(VACIO)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      // OJO: estos nombres se emparejan por POSICION con el arreglo de abajo.
      // Estuvieron corridos un lugar y nadie se entero: `promociones` recibia el
      // mapa de promociones-por-jugador (que no tiene .data) y quedaba SIEMPRE
      // vacio -- el selector de "Asignar promocion" decia que no habia ninguna
      // aunque estuvieran creadas. Si agregas una llamada, agregala al final de
      // las dos listas.
      const [
        asigRes, planesRes, planesDe, jugadoresRes, familiasRes, gruposRes, inscritosPorGrupo,
        adeudoDe, promocionesRes, promocionesDe, orgDeportesRes,
      ] = await Promise.all([
        getTodasAsignaciones(temporadaId),
        getPlanes({ temporadaId }),
        getPlanesDeJugadores(temporadaId),
        getAllPlayersWithGroups(),
        getFamilias(),
        getAllGroups(),
        getInscritosPorGrupo(temporadaId),
        getJugadoresConAdeudo(temporadaId),
        getPromociones({ temporadaId }),
        getPromocionesDeJugadores(temporadaId),
        getOrgDeportes(org?.id),
      ])

      // Solo grupos del DEPORTE de la temporada: getInscritosPorGrupo ya aplica
      // ese filtro adentro, asi que sus claves son la lista de grupos validos.
      // Sin esto, elegir un grupo de tackle cobraria el plan de flag (el cruce
      // con el padron no lo impide: el jugador si esta inscrito, solo que en
      // otro deporte).
      const validos = new Set(Object.keys(inscritosPorGrupo))

      setData({
        asignaciones: asigRes.data || [],
        planes: planesRes.data || [],
        planesDe: planesDe || {},
        jugadores: (jugadoresRes.data || []).filter(isVisiblePlayer),
        familias: familiasRes.data || [],
        grupos: (gruposRes.data || []).filter((g) => validos.has(g.id)),
        inscritosPorGrupo: inscritosPorGrupo || {},
        adeudoDe: adeudoDe || new Set(),
        orgDeportes: orgDeportesRes.data || [],
        promociones: promocionesRes.data || [],
        promocionesDe: promocionesDe || {},
      })
    } catch (e) {
      setData(VACIO)
      setError(e?.message || 'No se pudieron cargar las asignaciones.')
    } finally {
      setLoading(false)
    }
  }, [temporadaId, org?.id])

  useEffect(() => { load() }, [load])

  // OJO: son dos mapas distintos con la misma forma.
  //   gruposDe        -> jugadorId: [grupoId, ...]   (IDS, para filtrar)
  //   gruposNombresDe -> jugadorId: [nombre, ...]    (NOMBRES, para pintar)
  // Cruzarlos pinta UUIDs en pantalla o deja el filtro sin acertar en silencio.
  const mapas = useMemo(() => {
    const gruposDe = {}
    const gruposNombresDe = {}
    const deportesDe = {}
    data.jugadores.forEach((j) => {
      const rels = (j.jugadores_grupos || []).filter(isActiveGroupRelation)
      gruposDe[j.id] = [...new Set(rels.map((r) => r.grupo_id || r.grupos?.id).filter(Boolean))]
      gruposNombresDe[j.id] = [...new Set(rels.map((r) => r.grupos?.nombre_del_grupo).filter(Boolean))]
      deportesDe[j.id] = [...new Set(rels.map((r) => r.grupos?.deporte).filter(Boolean))]
    })
    return { gruposDe, gruposNombresDe, deportesDe }
  }, [data.jugadores])

  return { ...data, ...mapas, loading, error, reload: load }
}

export default useAsignacionesData
